/* Handles stripe related functions/state such as stripe elements setup, token creation */
import {
  STRIPE_INIT,
  STRIPE_ELEMENT_INIT,
  STRIPE_ERROR
} from '@stores/mutation-types';
import { STRIPE_PAYMENT_GATEWAY_TYPE_ID } from '@constants';
import {
  Main as Donation
} from '@services/App/donation';

const state = () => ({
  /**
   * Initialized stripe connection
   * Initialization will only happen once even if you import the stripe element component multiple times
   */
  stripe: null,

  /**
   * Element
   */
  elements: {

    /**
     * Main stripe element library
     */
    groups: {},

    /**
     * Type that currently supported that we need.
     * Add more types if needed.
     */
    types: [
      'card',
      'cardNumber',
      'cardExpiry',
      'cardCvc'
    ],

    /**
     * Default style on creating an element.
     * Customized this style using component prop.
     */
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '15px',
        '::placeholder': {
          color: '#aab7c4'
        },
        lineHeight: '23px'
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  },

  errors: null
});

/**
 * Handles module states manipulation
 */
const mutations = {

  /**
   * @param {object} state stripe module states
   * @param {object} stripe
   *
   * @return void
   */
  [STRIPE_INIT]: (state, stripe) => {
    state.stripe = stripe;
  },

  /**
   * @param {object} state stripe module states
   * @param {object} name
   *
   * @return void
   */
  [STRIPE_ELEMENT_INIT]: (state, name) => {
    if (!state.elements.groups[name]) {
      state.elements.groups[name] = state.stripe.elements();
    }
  },

  /**
   * @param {object} state stripe module states
   * @param {string} message
   *
   * @return void
   */
  [STRIPE_ERROR]: (state, message) => {
    state.errors = message;
  }

};

const actions = {

  /**
   * Initialize stripe connection using Stripe.js.
   *
   * @param {object} context Vuex context keys inlcudes ( commit, state, getters, dispatch ).
   * @param {object} payload Contains all stripe initialization settings. Like locale.
   *
   * @return {promise}
   */
  async stripeInitialize({ state, commit, dispatch }, payload) {

    if (state.stripe && !payload.forced) {
      return;
    }

    const { success, data } = await dispatch('getPublishableKey');
    if (!success) {
      return commit(STRIPE_ERROR, 'Failed fetching publishable keys');
    }

    return commit(STRIPE_INIT, Stripe(data.publishable_key, payload));

  },

  /**
   * Initialize stripe element using Stripe.js.
   *
   * @param {object} context Vuex context keys
   * @param {string} name Name where the element will belong
   *
   * @return {promise}
   */
  async stripeElementInitialize({ state, commit, dispatch }, name) {

    if (!state.stripe) {
      await dispatch('stripeInitialize');
    }

    return commit(STRIPE_ELEMENT_INIT, name);

  },

  /**
   * Create stripe card token.
   *
   * @param {object} context Vuex context keys
   * @param {string} groupKey group key of the element registered
   *
   * @return {promise}
   */
  async stripeCreateToken({ state: { stripe, elements }}, groupKey) {

    const group = elements.groups[groupKey];
    const result = await stripe.createToken(
      group.getElement('card')
    );

    return result;

  },

  /**
   * Create stripe card token.
   *
   * @param {object} context Vuex context keys
   * @param {string} payload All infromation needed by confirming card payment
   *
   * @return {promise}
   */
  async stripeConfirmPayment({ state, dispatch }, payload) {
    if (!state.stripe) {
      await dispatch('stripeInitialize', {});
    }

    return await state.stripe.confirmCardPayment(
      payload.client_secret,
      payload.params
    );
  },

  /**
   * Create stripe payment method.
   *
   * @param {object} context Vuex context keys
   * @param {string} payload All infromation needed by creating payment method
   *
   * @return {promise}
   */
  async stripeCreatePaymentMethod({ state: { stripe, elements } }, payload) {

    const element = elements.groups[payload.group];
    const result = await stripe.createPaymentMethod({
      type: 'card',
      card: element.getElement('card'),
      billing_details: payload.billing_details
    });

    return result;

  },

  /**
   * Confirm setup intent
   *
   * @param {object} context Vuex context keys
   * @param {string} payload All infromation needed by confirming card payment
   *
   * @return {promise}
   */
  async stripeConfirmSetupIntent({ state, dispatch }, payload) {
    if (!state.stripe) {
      await dispatch('stripeInitialize', {});
    }

    return await state.stripe.confirmCardSetup(payload.client_secret);
  },

  /**
   * Get applications Stripe.js publishable key.
   *
   * @param {object} context Vuex context.
   *
   * @return {promise} publishable keys.
   */
  async getPublishableKey({ commit }) {
    return await Donation.getPublishableKey(
      STRIPE_PAYMENT_GATEWAY_TYPE_ID
    );
  }

};

const getters = {};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};